import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from "react-icons/fa";
import Bg from "../Images/contact.jpg";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const darkGlassStyle = {
    background: "rgba(0, 0, 36, 0.85)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(0, 217, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  };

  const contactInfo = [
    {
      icon: <FaEnvelope size={24} />,
      title: "Email Us",
      detail: "ictaznipa@gmail.com",
      color: "#00d9ff",
    },
    {
      icon: <FaPhone size={24} />,
      title: "Call Us",
      detail: "+260 XXX XXX XXX",
      color: "#00d9ff",
    },
    {
      icon: <FaMapMarkerAlt size={24} />,
      title: "Visit Us",
      detail: "NIPA, Lusaka, Zambia",
      color: "#00ff88",
    },
  ];

  const socialLinks = [
    { icon: <FaFacebook size={20} />, link: "#", label: "Facebook" },
    { icon: <FaTwitter size={20} />, link: "#", label: "Twitter" },
    { icon: <FaLinkedin size={20} />, link: "#", label: "LinkedIn" },
    { icon: <FaInstagram size={20} />, link: "#", label: "Instagram" },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Message Sent:", formData);
    setSnackbar({ open: true, message: "Message sent successfully! We'll get back to you soon.", severity: "success" });
    setFormData({ name: "", email: "", subject: "", message: "" });
  };

  const inputStyle = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "12px",
      color: "white",
      "& fieldset": {
        borderColor: "rgba(255, 255, 255, 0.2)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 217, 255, 0.5)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#00d9ff",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.6)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#00d9ff",
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, rgba(0, 0, 36, 0.95) 0%, rgba(10, 25, 47, 0.9) 50%, rgba(0, 0, 36, 0.95) 100%), url(${Bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        color: "white",
        pt: { xs: 12, md: 14 },
        pb: 8,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Particles */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: "absolute",
              width: Math.random() * 4 + 2,
              height: Math.random() * 4 + 2,
              borderRadius: "50%",
              background: "rgba(0, 217, 255, 0.4)",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, 20],
              opacity: [0.2, 0.7, 0.2],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2,
            }}
          />
        ))}
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              fontWeight: 800,
              mb: 2,
              background: "linear-gradient(90deg, #ffffff, #00d9ff)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Get In Touch
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.7)",
              mb: 6,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Have questions or want to collaborate? We'd love to hear from you!
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {/* Contact Info Cards */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    whileHover={{ x: 10 }}
                  >
                    <Card
                      sx={{
                        borderRadius: "20px",
                        ...darkGlassStyle,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          border: `1px solid ${info.color}50`,
                          boxShadow: `0 15px 40px ${info.color}20`,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3, display: "flex", alignItems: "center", gap: 3 }}>
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: "15px",
                              background: `linear-gradient(135deg, ${info.color}30, ${info.color}10)`,
                              border: `2px solid ${info.color}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: info.color,
                            }}
                          >
                            {info.icon}
                          </Box>
                        </motion.div>
                        <Box>
                          <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.6)", mb: 0.5 }}>
                            {info.title}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: "white" }}>
                            {info.detail}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}

                {/* Social Links */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <Card
                    sx={{
                      borderRadius: "20px",
                      ...darkGlassStyle,
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Follow Us
                      </Typography>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        {socialLinks.map((social, index) => (
                          <motion.a
                            key={index}
                            href={social.link}
                            whileHover={{ scale: 1.2, y: -5 }}
                            whileTap={{ scale: 0.9 }}
                            style={{ textDecoration: "none" }}
                          >
                            <Box
                              sx={{
                                width: 45,
                                height: 45,
                                borderRadius: "12px",
                                background: "rgba(0, 217, 255, 0.1)",
                                border: "1px solid rgba(0, 217, 255, 0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#00d9ff",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: "#00d9ff",
                                  color: "white",
                                },
                              }}
                            >
                              {social.icon}
                            </Box>
                          </motion.a>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            </motion.div>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Card
                sx={{
                  borderRadius: "25px",
                  ...darkGlassStyle,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: "12px",
                          background: "linear-gradient(135deg, #00d9ff, #00b4d8)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FaPaperPlane size={22} color="white" />
                      </Box>
                    </motion.div>
                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                      Send Us a Message
                    </Typography>
                  </Box>

                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          sx={inputStyle}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          sx={inputStyle}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          sx={inputStyle}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Your Message"
                          name="message"
                          multiline
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          sx={inputStyle}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <motion.div
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            startIcon={<FaPaperPlane />}
                            sx={{
                              mt: 2,
                              py: 1.8,
                              borderRadius: "15px",
                              background: "linear-gradient(90deg, #00d9ff, #00b4d8)",
                              fontSize: "1.1rem",
                              fontWeight: 600,
                              textTransform: "none",
                              boxShadow: "0 4px 20px rgba(0, 217, 255, 0.4)",
                              "&:hover": {
                                background: "linear-gradient(90deg, #00b4d8, #00d9ff)",
                                boxShadow: "0 6px 30px rgba(0, 217, 255, 0.6)",
                              },
                            }}
                          >
                            Send Message
                          </Button>
                        </motion.div>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Map Section Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Card
            sx={{
              mt: 6,
              borderRadius: "25px",
              ...darkGlassStyle,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box
                sx={{
                  height: 300,
                  background: "linear-gradient(135deg, rgba(0, 217, 255, 0.1), rgba(0, 0, 36, 0.9))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <motion.div
                  animate={{ y: [-5, 5, -5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <FaMapMarkerAlt size={50} color="#00d9ff" />
                </motion.div>
                <Typography variant="h5" fontWeight={600} sx={{ color: 'white' }}>
                  NIPA Campus, Lusaka
                </Typography>
                <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                  National Institute of Public Administration
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </motion.div>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
